import FormField from "@/components/shared/form/form_field";
import FormInputRecord from "@/components/shared/form/form_input_record";
import InputPhoneNumber from "@/components/shared/form/inputs/input-phone-number";
import PasswordStrengthBar from "@/components/shared/form/password_strength_bar";
import ScrollToError from "@/components/shared/form/scroll_to_error";
import * as validation from "@/components/shared/validation";
import { FormikWrapper } from "@/components/shared/wrappers/formik-wrapper";
import LanguageWrapper from "@/components/shared/wrappers/language-wrapper";
import { set_step_1 } from "@/redux/sign-up/sign-up.methods";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LoginIcon from "@mui/icons-material/Login";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useFormikContext } from "formik";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import Page from "../../components/shared/page";
import Header from "../../components/sign-up/header";
import Stepper from "../../components/sign-up/stepper";

export default function SignUp() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { step_1 } = useSelector((state) => state.sign_up_state);

	const initialValues = {
		dial_code: "+31",
		email_address: "",
		family_name: "",
		given_name: "",
		has_debtor_code: "no",
		language: "",
		middle_name: "",
		password: "",
		password_repeat: "",
		phone_number_digits: "",
		salutation: "male",
		username: "",
	};

	const validationSchema = Yup.object().shape({
		dial_code: validation.inputRequired(t),
		email_address: validation.emailAddressMultiple(t),
		family_name: validation.inputName(t),
		given_name: validation.inputName(t),
		middle_name: validation.inputNameOptional(t),
		password: validation.inputPassword(t),
		password_repeat: validation.inputPasswordNoRegexCheck(t),
		phone_number_digits: validation.phoneNumberDigits(t),
		username: validation.inputUsername(t, 5, 50),
	});

	/* ---------------------------------- HOOKS --------------------------------- */
	const router = useRouter();
	const [initValues, setInitValues] = useState(initialValues);

	/* -------------------------------- HANDLERS -------------------------------- */
	const createRedirectUrl = (url) => {
		const queryParams = new URLSearchParams(router.query);
		return `${url}?${queryParams.toString()}`;
	};
	const updateValuesInRedux = (values) => dispatch(set_step_1(values));
	const updateHandler = (values, url) => {
		updateValuesInRedux(values);
		const redirectUrl = createRedirectUrl(url);
		router.push(redirectUrl);
	};
	const isPasswordMatchRepeatPassword = (values) => {
		return values.password === values.password_repeat;
	};

	const returnHandler = (values) => updateHandler(values, "/sign-in");
	const submitHandler = (values, { setFieldError }) => {
		if (!isPasswordMatchRepeatPassword(values)) {
			return setFieldError("password_repeat", t("validation.password.no_match"));
		}
		return updateHandler(values, "/sign-up/step-2");
	};

	/* -------------------------------- LISTENERS ------------------------------- */
	useEffect(() => {
		if (step_1) {
			setInitValues(step_1);
		}
	}, [step_1]);

	return (
		<LanguageWrapper>
			<Page title="Sign-up">
				<Header />

				<Grid container sx={{ mb: 5, px: { lg: 10, md: 5, xl: 40, xs: 2.5 } }} columnSpacing={5}>
					{/* --------------------------- STEPPER / PROGRESSBAR -------------------------- */}
					<Grid item xs={12} sx={{ my: 5 }}>
						<Stepper active_step={0} />
					</Grid>

					{/* ---------------------------------- FORM ---------------------------------- */}
					<Grid item xs={12}>
						<Grid container display="flex" justifyContent="center">
							<Grid item xs={11} md={10}>
								<Card sx={{ px: { md: 4, xs: 2 }, py: 3 }} variant="outlined">
									<FormikWrapper
										initialValues={initValues}
										validationSchema={validationSchema}
										onSubmit={submitHandler}
										enableReinitialize
									>
										<FormSectionUserDetails />
										<FormSectionAccountDetails return_handler={returnHandler} />

										<ScrollToError />
									</FormikWrapper>
								</Card>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Page>
		</LanguageWrapper>
	);
}

/* -------------------------------------------------------------------------- */
/*                          FORM SECTION USER DETAILS                         */
/* -------------------------------------------------------------------------- */
const FormSectionUserDetails = () => {
	const { t } = useTranslation();
	const { values } = useFormikContext();

	const hasDebtorCode = () => values.has_debtor_code === "yes";

	return (
		<>
			<Typography fontWeight={500} fontSize={24}>
				{t("sign_up.form.titles.debtor")}
			</Typography>

			<Typography>{t("sign_up.form.descriptions.debtor")}</Typography>

			<Divider sx={{ my: 2 }} />
			{/* ---------------------------- HAS DEBTOR NUMBER --------------------------- */}
			<FormInputRecord
				label={t("sign_up.form.labels.has_debtor_code")}
				no_divider
				required
				size="small"
				dense
				tip={t("sign_up.form.tips.debtor_code")}
			>
				<FormField
					type="radio"
					name="has_debtor_code"
					size="small"
					options={[
						{
							label: t("sign_up.form.values.yes"),
							value: "yes",
						},
						{
							label: t("sign_up.form.values.no"),
							value: "no",
						},
					]}
				/>
			</FormInputRecord>

			{/* ---------------------------- HAS DEBTOR ALERT ---------------------------- */}
			{hasDebtorCode() ? (
				<AlertDebtorCodeAvailable />
			) : (
				<>
					{/* --------------------------------- GENDER --------------------------------- */}
					<FormInputRecord
						label={t("inputs.salutation.label")}
						required
						no_divider
						size="small"
						dense
					>
						<FormField
							type="radio"
							name="salutation"
							size="small"
							options={[
								{
									label: t("inputs.salutation.values.male"),
									value: "male",
								},
								{
									label: t("inputs.salutation.values.female"),
									value: "female",
								},
							]}
						/>
					</FormInputRecord>

					{/* ------------------------------- FIRST NAME ------------------------------- */}
					<FormInputRecord
						label={t("inputs.given_name.label")}
						name="given_name"
						placeholder={t("inputs.given_name.placeholder")}
						required
						no_divider
						size="small"
						dense
					/>

					{/* ------------------------------- MIDDLE NAME ------------------------------ */}
					<FormInputRecord
						label={t("inputs.middle_name.label")}
						name="middle_name"
						placeholder={t("inputs.middle_name.placeholder")}
						no_divider
						dense
						size="small"
					/>

					{/* ------------------------------- FAMILY NAME ------------------------------ */}
					<FormInputRecord
						label={t("inputs.family_name.label")}
						name="family_name"
						no_divider
						placeholder={t("inputs.family_name.placeholder")}
						required
						dense
						size="small"
					/>

					{/* ------------------------------- E-MAILADRES ------------------------------ */}
					<FormInputRecord
						label={t("inputs.email_address.label")}
						name="email_address"
						type="email"
						no_divider
						placeholder={t("inputs.email_address.placeholder")}
						required
						dense
						size="small"
					/>

					{/* ------------------------------ PHONE NUMBER ------------------------------ */}
					<FormInputRecord label={t("inputs.phone_number.label")} required dense no_divider>
						<InputPhoneNumber
							name="phone_number_digits"
							name_dial_code="dial_code"
							label={t("inputs.phone_number.label")}
							placeholder={t("inputs.phone_number.placeholder")}
							size="small"
						/>
					</FormInputRecord>

					<Grid item xs={12}>
						<Divider sx={{ my: 2.5 }} />
					</Grid>
				</>
			)}
		</>
	);
};

function AlertDebtorCodeAvailable() {
	const { t } = useTranslation();

	return (
		<Grid container sx={{ mb: 2.5 }}>
			<Grid item xs={12}>
				<Alert
					severity="info"
					variant="outlined"
					action={
						<Link href="/sign-in" passHref>
							<Button
								color="primary"
								size="medium"
								endIcon={<LoginIcon />}
								variant="outlined"
								disableElevation
							>
								{t("buttons.sign_in")}
							</Button>
						</Link>
					}
				>
					<AlertTitle>{t("sign_up.form.debtor_info.title")}</AlertTitle>

					<Typography variant="body2">
						<Trans
							i18nKey="sign_up.form.debtor_info.description"
							components={{
								password_reset_link: (
									<Link
										href="https://support.tuxis.nl/nl/knowledgebase/article/wachtwoord-herstellen-klantenportaal"
										target="_blank"
										rel="noopener norefferer"
									/>
								),
							}}
						/>
					</Typography>
				</Alert>
			</Grid>
		</Grid>
	);
}

/* -------------------------------------------------------------------------- */
/*                           ACCOUNT DETAILS SECTION                          */
/* -------------------------------------------------------------------------- */
const FormSectionAccountDetails = ({ return_handler }) => {
	const { t } = useTranslation();

	const { values } = useFormikContext();

	const hasDebtorCode = () => values.has_debtor_code === "yes";

	if (hasDebtorCode()) {
		return null;
	}

	return (
		<>
			<Typography fontWeight={500} fontSize={24}>
				{t("sign_up.form.titles.user")}
			</Typography>

			<Divider sx={{ my: 2 }} />
			{/* -------------------------------- USERNAME -------------------------------- */}
			<FormInputRecord
				label={t("inputs.username.label")}
				name="username"
				placeholder={t("inputs.username.new_user_placeholder")}
				required
				no_divider
				size="small"
				dense
				end_adornment="@DB*****"
			/>

			{/* -------------------------------- PASSWORD -------------------------------- */}
			<FormInputRecord
				label={t("inputs.password.label")}
				name="password"
				placeholder={t("inputs.password.placeholder")}
				required
				no_divider
				size="small"
				type="password"
				password_allow_show
				dense
			/>

			{/* ----------------------- PASSWORD STRENGTH INDICATOR ---------------------- */}
			{values.password !== "" && (
				<Grid container sx={{ mb: -2 }}>
					<Grid item xs={12} md={5} />
					<Grid item xs={12} md={7}>
						<PasswordStrengthBar password={values.password} />
					</Grid>
				</Grid>
			)}

			{/* ----------------------------- REPEAT PASSWORD ---------------------------- */}
			<FormInputRecord
				label={t("inputs.password_repeat.label")}
				name="password_repeat"
				placeholder={t("inputs.password_repeat.placeholder")}
				required
				type="password"
				no_divider
				size="small"
				dense
			/>

			{/* -------------------------- CONFIRMATION BUTTONS -------------------------- */}
			<Grid container>
				<Grid item xs={12}>
					<Divider sx={{ my: 2.5 }} />
				</Grid>
				<Grid item xs={12} display="flex" justifyContent="flex-end">
					<Button
						type="button"
						variant="outlined"
						onClick={() => return_handler(values)}
						color="grey"
						sx={{ mr: 1.5 }}
					>
						{t("buttons.cancel")}
					</Button>
					<Button type="submit" variant="contained" endIcon={<ArrowForwardIcon />} disableElevation>
						{t("buttons.next")}
					</Button>
				</Grid>
			</Grid>
		</>
	);
};
